/**
 * Acciones para listar clientes en la gestión de clientes
 * gc_lc_ = Gestión de Clientes (gc) _ Listar Clientes (lc)
 */
import {Dispatch} from 'react'
import * as http from '../../../api/http'
import {ENDPOINTS} from '../../../api/config'
import {State} from '../../../reducers'
import {
  generalOpenModalError,
  generalOpenModalInfo,
  generalOpenModalMessage,
  generalShowLoader,
} from '../../general.actions'
import {GESTION_CLIENTES_TYPES} from '../../../constants/gestionCliente.types'
import {GestionClienteAction} from '../../../interfaces/gestion-cliente.interface'
import {KEYS} from '../../../constants/keysStorage'
import {CATALOGOS_TYPES} from '../../../constants/catalogos.types'
import {quitarAcentos} from '../../../utils/strings.utils'

// ******************************************************************************************
// ? ACTION CREATORS
// ******************************************************************************************
export const gc_lc_SetArrayClientes = (clientes: Array<any>) => ({
  type: GESTION_CLIENTES_TYPES.LC_SET_ARRAY_CLIENTES,
  payload: clientes,
})

export const gc_lc_SetClientes = (clientes: Array<any>) => ({
  type: GESTION_CLIENTES_TYPES.LC_SET_CLIENTES,
  payload: clientes,
})

export const gc_lc_HandleChangeFormBuscar = (form: any) => ({
  type: GESTION_CLIENTES_TYPES.LC_FORM_BUSCAR,
  payload: form,
})

export const gc_lc_ClearFormBuscar = () => ({
  type: GESTION_CLIENTES_TYPES.LC_CLEAR_FORM_BUSCAR,
  payload: null,
})

export const gc_lc_Clear = () => ({
  type: GESTION_CLIENTES_TYPES.LC_CLEAR,
  payload: null,
})

export const gc_lc_OpenModalEstatus = (flag: boolean) => ({
  type: GESTION_CLIENTES_TYPES.LC_SHOW_MODAL_ESTATUS,
  payload: flag,
})

export const gc_lc_OpenModalEliminarCliente = (flag: boolean) => ({
  type: GESTION_CLIENTES_TYPES.LC_SHOW_MODAL_ELIMINAR_CLIENTE,
  payload: flag,
})

export const gc_lc_SetClienteSeleccionado = (cliente: any) => ({
  type: GESTION_CLIENTES_TYPES.LC_SET_CLIENTE_SELECCIONADO,
  payload: cliente,
})

// ******************************************************************************************
// ? FUNCIONES O PROCEDIMIENTOS SIN PETICIONES HTTP
// ******************************************************************************************
export const gc_lc_FormBuscarHandleChange =
  (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
  async (dispatch: Dispatch<GestionClienteAction>) => {
    let {name, value} = e.target
    dispatch(gc_lc_HandleChangeFormBuscar({[name]: value}))
  }

export const gc_lc_BuscarCliente =
  (flag: boolean = false) =>
  async (dispatch: Dispatch<GestionClienteAction>, getState: () => State) => {
    let arrayClientes = getState().gestionCliente.listarClientes.arrayClientes
    if (arrayClientes.length === 0 && !flag)
      return dispatch(generalOpenModalError('No se encontró el cliente'))

    const {nombre, email, telefono, numId, tipoDocumento} =
      getState().gestionCliente.listarClientes.formBuscar

    if (!nombre && !email && !telefono && !numId && !tipoDocumento) {
      return dispatch(gc_lc_SetClientes(arrayClientes))
    }

    let newClientes: Array<any> = []

    arrayClientes.forEach((cliente) => {
      let encontrado = true

      if (nombre) encontrado = encontrado && quitarAcentos(cliente?.nombre ?? '').includes(quitarAcentos(nombre))
      if (email) encontrado = encontrado && quitarAcentos(cliente?.email ?? '').includes(quitarAcentos(email))
      if (telefono) encontrado = encontrado && (cliente?.telefono ?? '').includes(telefono)
      if (numId) encontrado = encontrado && quitarAcentos(cliente?.numId ?? '').includes(quitarAcentos(numId))
      if (tipoDocumento) encontrado = encontrado && cliente?.tipoDocumento?.toString() === tipoDocumento

      if (encontrado) newClientes.push(cliente)
    })

    dispatch(gc_lc_SetClientes(newClientes))
  }

// ******************************************************************************************
// ? PETICIONES HTTP
// ******************************************************************************************

const formatearFechaHora = (fecha: string | null | undefined): string => {
  if (!fecha) return '---'
  try {
    const d = new Date(fecha)
    if (isNaN(d.getTime())) return fecha
    const dd = String(d.getDate()).padStart(2, '0')
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const yyyy = d.getFullYear()
    return `${dd}/${mm}/${yyyy}`
  } catch {
    return fecha ?? '---'
  }
}

/**
 * Carga los municipios únicamente para los departamentos que aparecen en los datos de clientes.
 * Nunca hace más requests que departamentos únicos presentes en la data.
 * Usa el cache persistido en Redux para no repetir peticiones en navegaciones subsecuentes.
 */
const cargarMunicipiosNecesarios = async (
  dispatch: Dispatch<any>,
  getState: () => State,
  codigosDepartamentos: string[],
) => {
  if (codigosDepartamentos.length === 0) return

  const cached = getState().catalogos.municipiosPorDepartamento
  const deptos = getState().catalogos.departamentos

  const sinCache = codigosDepartamentos.filter((code) => code && !cached[code])
  if (sinCache.length === 0) return

  await Promise.all(
    sinCache.map(async (deptCode) => {
      const dept = deptos.find((d: any) => d.codigo === deptCode)
      try {
        const res: any = await http.post(ENDPOINTS.GET_MUNICIPIOS, {
          codigo: deptCode,
          valor: dept?.valor ?? '',
        })
        const municipios = res?.body ?? res?.data
        if (municipios && Array.isArray(municipios)) {
          dispatch({
            type: CATALOGOS_TYPES.CATALOGO_SET_MUNICIPIOS_POR_DEPTO,
            payload: {deptCode, municipios},
          })
        }
      } catch {
        // Silenciar error; el nombre del municipio quedará como código
      }
    }),
  )
}

export const gc_lc_GetClientes = () => async (dispatch: Dispatch<any>, getState: () => State) => {
  if (getState().general.loader) return
  dispatch(generalShowLoader(true))
  await getClientes(dispatch, getState)
  await dispatch(generalShowLoader(false))
}

const transformClienteData = (
  cliente: any,
  deptMap: Record<string, string>,
  muniMap: Record<string, string>,
  tipoDocMap: Record<string, string>
) => {
  const activo = cliente.activo === 'True' || cliente.activo === true ? 'Activo' : 'Deshabilitado'
  const estado_color = activo === 'Activo' ? '#d4edda' : '#f8d7da'

  const nombreDepartamento = deptMap[String(cliente.departamento)] ?? cliente.departamento
  const nombreMunicipio = muniMap[`${cliente.departamento}|${cliente.municipio}`] ?? cliente.municipio

  return {
    ...cliente,
    tipoDocumento: cliente.tipodocumento,
    idcliente: cliente.iddatoscliente,
    numId: cliente.numdocumento,
    nombreTipoDocumento: tipoDocMap[cliente.tipodocumento] || cliente.tipodocumento,
    nombreDepartamento,
    nombreMunicipio,
    actividad: cliente.codactividad,
    descripcion: cliente.descactividad,
    fechaCreacion: formatearFechaHora(cliente.fechacreacion),
    activo,
    estado_color,
  }
}

const getClientes = async (
  dispatch: Dispatch<any>,
  getState: () => State,
  msg: string = '',
) => {
  const codigoempresa = localStorage.getItem(KEYS.CODIGO_EMPRESA)
  const username = getState().userLogin.datos.username
  const message = msg || 'No se logró obtener información de los clientes registrados.'

  await http
    .get(`${ENDPOINTS.GESTION_CLIENTES_GET_CLIENTES}?codigoempresa=${codigoempresa}&username=${username}`)
    .then(async (res: any) => {
      if (!res?.data || !Array.isArray(res.data) || res.data.length === 0) {
        dispatch(gc_lc_SetArrayClientes([]))
        dispatch(gc_lc_SetClientes([]))
        dispatch(generalOpenModalInfo(res?.data?.length === 0 ? 'No existen clientes registrados' : message))
        return
      }

      // Obtener departamentos únicos presentes en los clientes → mínimo de requests
      const deptosUnicos = Array.from(new Set<string>(res.data.map((c: any) => c.departamento).filter(Boolean)))
      await cargarMunicipiosNecesarios(dispatch, getState, deptosUnicos)

      // Construir mapas de lookup desde catálogos (sin requests adicionales)
      const deptos = getState().catalogos.departamentos
      const deptMap: Record<string, string> = {}
      deptos.forEach((d: any) => { deptMap[String(d.codigo)] = d.valor })

      const tiposDoc = getState().catalogos.tipoDocumento
      const tipoDocMap: Record<string, string> = {}
      tiposDoc.forEach((t: any) => { tipoDocMap[t.codigo] = t.valor })

      const allMunis = getState().catalogos.municipiosPorDepartamento
      const muniMap: Record<string, string> = {}
      res.data.forEach((c: any) => {
        const key = `${c.departamento}|${c.municipio}`
        if (!muniMap[key]) {
          const found = allMunis[c.departamento]?.find((m: any) => String(m.codigo) === String(c.municipio))
          muniMap[key] = found?.valor ?? c.municipio
        }
      })

      const clientes = res.data
        .map((c: any) => transformClienteData(c, deptMap, muniMap, tipoDocMap))
        .sort((a: any, b: any) => Number(b.idcliente) - Number(a.idcliente))

      dispatch(gc_lc_SetArrayClientes(clientes))
      dispatch(gc_lc_SetClientes(clientes))
    })
    .catch(async (err: any) => {
      await dispatch(generalOpenModalError(err?.response?.data?.message ?? message, err?.response?.data?.status))
    })
}

export const gc_lc_EliminarClienteConfirm =
  () => async (dispatch: Dispatch<any>, getState: () => State) => {
    if (getState().general.loader) return
    dispatch(generalShowLoader(true))

    const clienteSeleccionado = getState().gestionCliente.listarClientes.clienteSeleccionado

    if (!clienteSeleccionado?.idcliente) {
      dispatch(generalShowLoader(false))
      return dispatch(generalOpenModalError('No se ha seleccionado un cliente'))
    }

    const id_user = localStorage.getItem(KEYS.ID_CLIENT)

    await http
      .post(ENDPOINTS.GESTION_CLIENTES_DELETE_CLIENTE, {
        idcliente: clienteSeleccionado.idcliente,
        id_user: id_user,
      })
      .then(async (res: any) => {
        dispatch(gc_lc_OpenModalEliminarCliente(false))
        dispatch(gc_lc_SetClienteSeleccionado(null))
        await dispatch(
          generalOpenModalMessage(
            'Cliente Eliminado',
            res?.data?.message ?? 'El cliente ha sido eliminado exitosamente'
          )
        )
        // Actualizar array local sin re-fetch
        const arrayActual = getState().gestionCliente.listarClientes.arrayClientes
        const filtrados = arrayActual.filter(
          (c: any) => c.idcliente !== clienteSeleccionado.idcliente
        )
        dispatch(gc_lc_SetArrayClientes(filtrados))
        dispatch(gc_lc_SetClientes(filtrados))
      })
      .catch(async (err: any) => {
        await dispatch(
          generalOpenModalError(
            err?.response?.data?.message ?? 'No se pudo eliminar el cliente',
            err?.response?.data?.status
          )
        )
      })
      .finally(() => dispatch(generalShowLoader(false)))
  }

export const gc_lc_ChangeEstatusCliente =
  () => async (dispatch: Dispatch<any>, getState: () => State) => {
    if (getState().general.loader) return
    dispatch(generalShowLoader(true))

    const clienteSeleccionado = getState().gestionCliente.listarClientes.clienteSeleccionado
    const id_user = localStorage.getItem(KEYS.ID_CLIENT)

    if (!clienteSeleccionado?.idcliente) {
      dispatch(generalShowLoader(false))
      return dispatch(generalOpenModalError('No se ha seleccionado un cliente'))
    }

    const estaActivo = clienteSeleccionado.activo === 'Activo'
    const nuevoEstado = !estaActivo

    await http
      .post(ENDPOINTS.GESTION_CLIENTES_UPDATE_CLIENTE, {
        idcliente: clienteSeleccionado.idcliente,
        id_user: id_user,
        activo: nuevoEstado,
      })
      .then(async (res: any) => {
        dispatch(gc_lc_OpenModalEstatus(false))
        dispatch(gc_lc_SetClienteSeleccionado(null))
        const mensajeExito = estaActivo
          ? 'El cliente ha sido deshabilitado exitosamente'
          : 'El cliente ha sido activado exitosamente'
        await dispatch(
          generalOpenModalMessage(
            estaActivo ? 'Cliente Deshabilitado' : 'Cliente Activado',
            res?.data?.message ?? mensajeExito
          )
        )
        // Actualizar array local sin re-fetch
        const nuevoActivo = nuevoEstado ? 'Activo' : 'Deshabilitado'
        const nuevoColor = nuevoEstado ? '#d4edda' : '#f8d7da'
        const arrayActual = getState().gestionCliente.listarClientes.arrayClientes
        const actualizados = arrayActual.map((c: any) =>
          c.idcliente === clienteSeleccionado.idcliente
            ? {...c, activo: nuevoActivo, estado_color: nuevoColor}
            : c
        )
        dispatch(gc_lc_SetArrayClientes(actualizados))
        dispatch(gc_lc_SetClientes(actualizados))
      })
      .catch(async (err: any) => {
        await dispatch(
          generalOpenModalError(
            err?.response?.data?.message ?? 'No se pudo actualizar el estado del cliente',
            err?.response?.data?.status
          )
        )
      })
      .finally(() => dispatch(generalShowLoader(false)))
  }
